/**
 * Talent-pool card menu derivation — a **data-integrity boundary** (SPEC Req 17,
 * PLAN P10 / T8).
 *
 * The `PATCH /v1/talent-pool/:freelancerId` endpoint falls back to editing the
 * *colleague's shared record* when the caller has no own save. A wrongly-shown
 * "Modifica note" / "Cambia visibilità" / "Rimuovi" on a colleague card would
 * therefore silently mutate a teammate's data. To make that impossible, this
 * pure selector is the ONLY authorized source of a card's menu contents: the
 * menu UI (T12a) renders exactly what `poolCardMenuItems` returns and derives
 * nothing on its own. Every combination is frozen behind an exhaustive node
 * test before any UI consumes it.
 */

// Ordered, string-literal union of every menu action a pool card can offer.
// The literals double as the i18n key suffixes under `company.talent_pool.list.menu.*`.
export type PoolCardMenuItem =
  // read-only navigation (Feature 3b) — mutates nothing, valid on EVERY card
  | "find_similar"
  // own-record mutations (PATCH/DELETE) — NEVER valid on a colleague card
  | "edit_note"
  | "change_visibility"
  | "remove"
  // colleague-card action — a normal Feature 1 POST creating the caller's OWN record
  | "save_to_own";

// Inputs to the derivation, all plain booleans (decoupled from any payload type):
// - `savedByMe`      — ownership (compare `saved_by_profile_id` with the signed-in id).
// - `canShare`       — the caller's plan allows "shared" visibility (multi-seat).
// - `isDiscoverable` — the freelancer is still publicly available (else attenuated card).
export type PoolCardMenuInput = {
  savedByMe: boolean;
  canShare: boolean;
  isDiscoverable: boolean;
};

/**
 * Derive the ordered menu items for a pool card from ownership + plan + discoverability.
 *
 * Truth table (SPEC Req 17/18/20/21, PLAN T8; Feature 3b AC2/AC3):
 * - every combination leads with `find_similar` (read-only, ownership-independent)
 * - own + discoverable      → `["find_similar", "edit_note", ...(canShare ? ["change_visibility"] : []), "remove"]`
 * - own + non-discoverable  → `["find_similar", "edit_note", "remove"]` (Req 21 — no visibility toggle)
 * - colleague + discoverable     → `["find_similar", "save_to_own"]`
 * - colleague + non-discoverable → `["find_similar"]` (Req 20 — a save would be a guaranteed 404;
 *   3b's primary use case: the attenuated card's only live action)
 *
 * INVARIANT: when `savedByMe` is false the result never contains `edit_note`,
 * `change_visibility`, or `remove` (the PATCH colleague-fallback hazard).
 */
export function poolCardMenuItems(
  input: PoolCardMenuInput,
): PoolCardMenuItem[] {
  const { savedByMe, canShare, isDiscoverable } = input;

  // Colleague card: besides "find similar", the only safe action is adopting the
  // save into the caller's own pool — and only while still discoverable (else 404).
  if (!savedByMe) {
    return isDiscoverable ? ["find_similar", "save_to_own"] : ["find_similar"];
  }

  // Own card. "Cambia visibilità" is offered only on a discoverable card whose
  // plan can share; a non-discoverable own card keeps just edit + remove (Req 21).
  if (isDiscoverable && canShare) {
    return ["find_similar", "edit_note", "change_visibility", "remove"];
  }
  return ["find_similar", "edit_note", "remove"];
}

/**
 * Derive `savedByMe` by comparing the record's saver id with the signed-in
 * profile id. Both sides are `String()`-wrapped so a numeric id from the BE
 * still matches its string form. A missing saver id or a missing own id (auth
 * not yet resolved) yields `false` — a card is never falsely treated as owned.
 */
export function derivePoolCardOwnership(
  savedByProfileId: string | null | undefined,
  ownProfileId: string | null | undefined,
): boolean {
  if (savedByProfileId == null || ownProfileId == null) return false;
  return String(savedByProfileId) === String(ownProfileId);
}
